<template>
  <v-select
    v-model="selected"
    :options="options"
    :filterable="false"
    :placeholder="placeholder"
    :selectable="item => !item.disabled && !disabled"
    :disabled="disabled"
    :reduce="reduce"
    :loading="loading"
    :label="optionLabel"
    :clearable="clearable"
    :append-to-body="appendToBody"
    :calculate-position="calculatePosition"
    @open="onOpen"
    @close="onClose"
    @search="debounceSearch"
  >
    <template #option="option">
      <slot
        name="optionValue"
        :value="option"
      >
        {{ option[optionLabel] }}
      </slot>
    </template>
    <template #no-options="{ search, searching }">
      <slot
        name="no-options"
        :search="search"
        :searching="searching"
      >
        <template v-if="searching">
          <em>'{{ search }}'</em> tidak ditemukan
        </template>
        <em v-else>Masukkan keyword yang valid</em>
      </slot>
    </template>
    <template #list-footer>
      <li
        v-show="hasNextPage"
        ref="load"
        class="text-center flex justify-center items-center gap-2 h-[50px]"
      >
        <b-spinner
          small
          variant="secondary"
        />
        <span>Loading more options...</span>
      </li>
    </template>
  </v-select>
</template>

<script>
/**
 * Select component with infinite scroll capability.
 *
 * ## Props
 * @prop {Object | string | Number} defaultValue - The default value of the selected item.
 * @prop {Object | String | Number} value - The value of the selected item.
 * @prop {String} optionLabel - The Object key used to display the selected item.
 * @prop {Array} options - The list of items available for selection.
 * @prop {String} placeholder - A short guidance text displayed in the input field.
 * @prop {Boolean} hasNextPage - Indicates whether there are more pages of data available for loading.
 * @prop {Number} debounceTimeout - Delay time (in milliseconds) for search functionality.
 * @prop {Function} reduce - A function that transforms the input value before returning it.
 * @prop {Boolean} loading - Manually set the v-select's loading state.
 * @prop {Boolean} clearable - Indicates whether the select input can be cleared.
 * @prop {Boolean} appendToBody - Indicates whether the select input should be appended to the body.
 * @prop {Function} calculatePosition - A function that calculates the position of the select input.
 * @prop {Boolean} disabled - Indicates whether the select input is disabled.
 * @prop {Boolean} reduce - A function that transforms the input value before returning it.
 * @prop {Boolean} loading - Manually set the v-select's loading state.
 *
 * ## Events
 * @event input - Triggered when a user selects an item.
 * @param {Object | String | Number} payload - The selected item.
 *
 * @event on-search - Triggered when a user uses the search functionality of vue-select.
 * @param {String} query - The search query entered by the user.
 *
 * @event on-intersect - Triggered when the scroll position reaches the bottom of the container.
 *
 * @event on-close - Triggered when the container closed.
 */

import vSelect from 'vue-select'
import { BSpinner } from 'bootstrap-vue'

export default {
  name: 'InfiniteSelect',
  components: {
    vSelect,
    BSpinner,
  },
  props: {
    defaultValue: {
      type: [Object, String, Number, null],
      required: false,
      default: null,
    },
    value: {
      type: [Object, String, Number, null],
      required: false,
      default: null,
    },
    optionLabel: {
      type: String,
      default: 'label',
    },
    options: {
      type: Array,
      default: () => [],
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Pilih item',
    },
    hasNextPage: {
      type: Boolean,
      default: false,
      required: true,
    },
    debounceTimeout: {
      type: Number,
      default: 300,
      required: false,
    },
    reduce: {
      type: Function,
      default: option => option,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    appendToBody: {
      type: Boolean,
      default: false,
      required: false,
    },
    calculatePosition: {
      type: Function,
      default: () => {},
      required: false,
    },
  },
  data: () => ({
    observer: null,
    debounceSearch: null,
    lastScollPosition: 0,
    isOnSearch: false,
  }),
  computed: {
    selected: {
      get() {
        return this.normalizeSelection(this.value ?? _.cloneDeep(this.defaultValue) ?? null)
      },
      set(newValue) {
        this.$emit('input', this.normalizeSelection(newValue))
      },
    },
  },
  watch: {
    options: {
      handler() {
        if (!this.observer || !this.$refs.load) return
        this.$nextTick(() => {
          if (this.observer && this.$refs.load) {
            this.observer.observe(this.$refs.load)
          }
        })
      },
      deep: false,
    },
  },
  created() {
    this.debounceSearch = _.debounce(
      q => {
        this.isOnSearch = true
        this.$emit('on-search', q)
      },
      this.debounceTimeout,
    )
  },
  mounted() {
    this.observer = new IntersectionObserver(this.infiniteScroll)
  },
  updated() {
    if (!this.isOnSearch) {
      const dropdown = this.$refs.load?.closest('ul') || this.$el.querySelector('ul')
      if (dropdown) {
        dropdown.scrollTop = this.lastScollPosition
      }
    }
  },
  methods: {
    normalizeSelection(val) {
      if (_.isObject(val)) {
        return ((_.get(val, this.optionLabel) ?? '') !== '') ? val : null
      }
      if (_.isString(val)) {
        return val.trim() !== '' ? val : null
      }
      return val ?? null
    },
    async onOpen() {
      await this.$nextTick()
      this.observer.observe(this.$refs.load)
    },
    onClose() {
      this.observer.disconnect()
      this.$emit('on-close')
    },
    async infiniteScroll([{ isIntersecting, target }]) {
      if (isIntersecting && this.hasNextPage) {
        this.observer.unobserve(target)
        if (this.isOnSearch) this.isOnSearch = false
        const ul = target.offsetParent
        if (!ul) {
          this.observer.disconnect()
          return
        }
        this.$emit('on-intersect')
        this.lastScollPosition = ul.scrollTop + 50
      }
    },
  },
}
</script>
